import {
  useCallback,
  useEffect,
  useState
} from "react";

const API =
  "https://api.galaxylabsnyc.com/api/v1";

const unwrap = response =>
  response?.data?.data ??
  response?.data ??
  response ??
  {};

const collect = (value, key) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value !== "object") {
    return [];
  }

  if (Array.isArray(value[key])) {
    return value[key];
  }

  for (const child of Object.values(value)) {
    const result = collect(child, key);

    if (result.length) {
      return result;
    }
  }

  return [];
};

const hybridProducts = (products, pagination = {}) =>
  Object.assign(
    [...products],
    {
      products,
      pagination,
      data: {
        products,
        pagination
      }
    }
  );

const hybridCategories = (categories, pagination = {}) =>
  Object.assign(
    [...categories],
    {
      categories,
      pagination,
      data: {
        categories,
        pagination
      }
    }
  );

export const useGalaxyAdminProducts = (
  params = {}
) => {
  const {
    page = 1,
    limit = 20,
    search = "",
    categoryId = ""
  } = params || {};

  const [data, setData] = useState(
    hybridProducts([])
  );

  const [isLoading, setIsLoading] =
    useState(true);

  const [error, setError] =
    useState(null);

  const load = useCallback(
    async (overrides = {}) => {
      const next = {
        page,
        limit,
        search,
        categoryId,
        ...overrides
      };

      setIsLoading(true);
      setError(null);

      try {
        const query =
          new URLSearchParams({
            page: String(next.page || 1),
            limit: String(next.limit || 20),
            search: String(next.search || ""),
            categoryId:
              String(next.categoryId || "")
          });

        const response = await fetch(
          `${API}/User/get-all-products?${query}`,
          {
            cache: "no-store"
          }
        );

        const json =
          await response.json();

        if (!response.ok) {
          throw new Error(
            json?.message ||
            "Unable to load products"
          );
        }

        const payload =
          unwrap(json);

        const products =
          collect(payload, "products");

        const pagination =
          payload?.pagination ??
          json?.pagination ??
          json?.data?.pagination ??
          json?.data?.data?.pagination ??
          {};

        setData(
          hybridProducts(
            products,
            pagination
          )
        );

        return {
          data:
            hybridProducts(
              products,
              pagination
            )
        };

      } catch (err) {
        console.error(
          "Admin product list failed:",
          err
        );

        setError(err);
        setData(
          hybridProducts([])
        );

        return {
          error: err
        };

      } finally {
        setIsLoading(false);
      }
    },
    [
      page,
      limit,
      search,
      categoryId
    ]
  );

  useEffect(() => {
    load();
  }, [load]);

  return {
    data,
    isLoading,
    isFetching: isLoading,
    error,
    refetch: load
  };
};

export const useGalaxyAdminCategories = () => {
  const [data, setData] = useState(
    hybridCategories([])
  );

  const [isLoading, setIsLoading] =
    useState(true);

  const [error, setError] =
    useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API}/User/get-categories`,
        {
          cache: "no-store"
        }
      );

      const json =
        await response.json();

      if (!response.ok) {
        throw new Error(
          json?.message ||
          "Unable to load categories"
        );
      }

      const payload =
        unwrap(json);

      const categories =
        collect(
          payload,
          "categories"
        );

      const pagination =
        payload?.pagination ??
        json?.pagination ??
        json?.data?.pagination ??
        json?.data?.data?.pagination ??
        {};

      const result =
        hybridCategories(
          categories,
          pagination
        );

      setData(result);

      return {
        data: result
      };

    } catch (err) {
      console.error(
        "Admin category list failed:",
        err
      );

      setError(err);
      setData(
        hybridCategories([])
      );

      return {
        error: err
      };

    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return {
    data,
    isLoading,
    isFetching: isLoading,
    error,
    refetch: load
  };
};
